'use client';

import { onOneTrustGroupsUpdated } from '../utils';

type OrdergrooveMsiV2LoaderOptions = {
    onLoad: () => void;
    onError: () => void;
    isOgCookieSet: boolean;
    scriptUrl: string;
};

/**
 * OneTrust loader for Ordergroove MSI.
 * Returns cleanup for effect teardown.
 */
export const createOrdergrooveLoaderV2 = ({
    onLoad,
    onError,
    isOgCookieSet,
    scriptUrl,
}: OrdergrooveMsiV2LoaderOptions) => {
    if (!isOgCookieSet) return;

    let settled = false;
    let disposed = false;

    const matchesScript = (e: Event) => e.target instanceof HTMLScriptElement && e.target.src === scriptUrl;

    const cleanupDocListeners = () => {
        document.removeEventListener('load', onDocEvent, true);
        document.removeEventListener('error', onDocEvent, true);
    };

    // Capture-phase fires before any async callback, so we never miss a cached load.
    // Using a single handler for both events; same reference makes addEventListener idempotent.
    const onDocEvent = (e: Event) => {
        if (!matchesScript(e) || settled || disposed) return;
        settled = true;
        cleanupDocListeners();
        if (e.type === 'load') onLoad();
        else onError();
    };

    // Script already in DOM: preserve legacy behavior — treat as loaded.
    if (document.querySelector(`script[src="${scriptUrl}"]`)) {
        onLoad();
        return;
    }

    const insert = () => {
        if (settled || disposed) return;
        if (!window.OneTrust?.InsertScript) return;

        // Attach before InsertScript so we never miss the load event, even from cache.
        document.addEventListener('load', onDocEvent, true);
        document.addEventListener('error', onDocEvent, true);
        window.OneTrust.InsertScript(scriptUrl, 'body', null, null, 'C0003', true);
    };

    insert();
    const removeConsentListener = onOneTrustGroupsUpdated(insert);

    return () => {
        disposed = true;
        cleanupDocListeners();
        removeConsentListener();
    };
};
